import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Dices, Home, BookOpen, Users, Heart, Sparkles } from "lucide-react";

const EducationHomeschool = () => {
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Random Number Generator for Homeschool Families
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Free educational tools for homeschooling parents and co-ops. Make math engaging, manage multi-age learning, 
            and create fair, fun educational experiences for all grade levels from K-12.
          </p>
        </header>

        {/* Key Benefits */}
        <section className="mb-16">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6">
              <Home className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Multi-Age Learning</h3>
              <p className="text-muted-foreground">
                Easily adapt activities for multiple grade levels simultaneously. Generate age-appropriate number ranges and challenges for each child.
              </p>
            </Card>
            <Card className="p-6">
              <Heart className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-xl font-semibold mb-2">Engaging & Interactive</h3>
              <p className="text-muted-foreground">
                Transform routine drills into exciting games. Use randomness to keep children engaged and motivated during math practice.
              </p>
            </Card>
            <Card className="p-6">
              <Users className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Homeschool Co-op Ready</h3>
              <p className="text-muted-foreground">
                Perfect for group activities, fair turn-taking, team formations, and game-based learning in co-op settings.
              </p>
            </Card>
          </div>
        </section>

        {/* Activities by Subject */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Homeschool Activity Ideas</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <BookOpen className="w-10 h-10 text-primary mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Math & Number Sense (K-12)</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Elementary (K-5):</strong> Number recognition, skip counting, place value practice, mental math drills with random numbers</li>
                <li>• <strong>Middle Grades (6-8):</strong> Integer operations, fraction/decimal practice, order of operations challenges, percentage calculations</li>
                <li>• <strong>High School (9-12):</strong> Algebra practice with random coefficients, probability experiments, statistics projects, calculus applications</li>
                <li>• <strong>Living Math:</strong> Create real-world scenarios using random numbers (shopping budgets, measurement projects, recipe scaling)</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <Sparkles className="w-10 h-10 text-accent mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Games & Motivation</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Math Dice Games:</strong> Generate target numbers for multiplication, addition races, or factor finding challenges</li>
                <li>• <strong>Reward Systems:</strong> Random prize drawings, behavior chart incentives, completed assignment celebrations</li>
                <li>• <strong>Turn-Taking:</strong> Fair selection for who goes first in board games, read-aloud order, or choosing activities</li>
                <li>• <strong>Challenge Mode:</strong> Difficulty randomizer for spelling bees, vocabulary practice, or timed math challenges</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <Dices className="w-10 h-10 text-primary mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Science & Exploration</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Scientific Method:</strong> Random sampling for nature studies, randomized trials for experiments, data collection practice</li>
                <li>• <strong>Biology:</strong> Genetics probability simulations (Punnett squares), population sampling, species identification randomization</li>
                <li>• <strong>Chemistry:</strong> Randomize lab procedures order, molecular mass calculations, periodic table element challenges</li>
                <li>• <strong>Physics:</strong> Generate random measurements for motion experiments, probability in quantum mechanics introductions</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <BookOpen className="w-10 h-10 text-accent mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Cross-Curricular Ideas</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Language Arts:</strong> Random story prompts by number, vocabulary word selection, spelling practice, writing prompt generators</li>
                <li>• <strong>History/Geography:</strong> Random year selection for timeline practice, country/state capitals quiz, historical event matching</li>
                <li>• <strong>Art/Music:</strong> Random color palette selection, tempo/beat patterns, technique challenges, project idea generators</li>
                <li>• <strong>Physical Education:</strong> Workout randomizers, exercise duration, team sports selection, fitness challenge numbers</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Homeschool Co-op Activities */}
        <section className="mb-16 bg-card/30 backdrop-blur-sm rounded-lg p-8 border border-border/50">
          <h2 className="text-3xl font-bold text-center mb-6">Homeschool Co-op & Group Learning</h2>
          <div className="max-w-4xl mx-auto space-y-6">
            <div>
              <h4 className="text-xl font-semibold mb-3">Group Management</h4>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li>• Randomly divide students into small groups or teams for collaborative projects</li>
                <li>• Fair selection system for who presents first, line leader, or class helper</li>
                <li>• Random partner pairing for peer teaching, buddy reading, or lab partners</li>
                <li>• Rotation scheduler for stations, learning centers, or activity sequences</li>
              </ul>
            </div>
            <div>
              <h4 className="text-xl font-semibold mb-3">Educational Games</h4>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li>• <strong>Math Olympiad:</strong> Teams compete with random problem generation at appropriate difficulty levels</li>
                <li>• <strong>Spelling Bee Format:</strong> Random word selection, fair elimination order, bonus round determination</li>
                <li>• <strong>Geography Bee:</strong> Random question order, student selection for answers, tiebreaker questions</li>
                <li>• <strong>Trivia Challenges:</strong> Generate random point values, category selection, team turn order</li>
              </ul>
            </div>
            <div>
              <h4 className="text-xl font-semibold mb-3">Fair Assessment</h4>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li>• Randomize quiz question order to prevent cheating in group testing</li>
                <li>• Generate random problem sets so each student has unique practice</li>
                <li>• Fair oral exam order selection to reduce anxiety about going first/last</li>
                <li>• Random assignment of project topics or research subjects</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Tools Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-6">Free Homeschool Tools</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/">
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                <Sparkles className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Number Generator</h3>
                <p className="text-muted-foreground">Generate numbers in any range perfect for all grade levels and math activities</p>
              </Card>
            </Link>
            <Link to="/games">
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                <Dices className="w-12 h-12 text-accent mb-4" />
                <h3 className="text-xl font-semibold mb-2">Dice & Games</h3>
                <p className="text-muted-foreground">Virtual dice rollers and coin flips for game-based learning and probability lessons</p>
              </Card>
            </Link>
            <Link to="/lists">
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                <Users className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">List Shuffler</h3>
                <p className="text-muted-foreground">Randomly organize student names, chores, activities, or any custom list</p>
              </Card>
            </Link>
          </div>
        </section>

        {/* Why Homeschoolers Love It */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Why Homeschool Families Choose Our Tools</h2>
          <div className="max-w-4xl mx-auto bg-card/50 backdrop-blur-sm rounded-lg p-8 border border-border/50">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-primary" />
                  Parent-Friendly Features
                </h4>
                <ul className="space-y-1 text-muted-foreground text-sm">
                  <li>✓ No login, registration, or personal data required</li>
                  <li>✓ 100% free forever - no hidden costs or subscriptions</li>
                  <li>✓ Clean interface with no ads or distractions</li>
                  <li>✓ Works on any device - desktop, tablet, or phone</li>
                  <li>✓ Safe for children - no external links or pop-ups</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-accent" />
                  Educational Value
                </h4>
                <ul className="space-y-1 text-muted-foreground text-sm">
                  <li>✓ Adaptable for all ages and curriculum styles</li>
                  <li>✓ Supports Charlotte Mason, Classical, Unit Studies</li>
                  <li>✓ Aligns with state standards and testing requirements</li>
                  <li>✓ Encourages mathematical thinking and probability concepts</li>
                  <li>✓ Perfect for delight-directed and hands-on learning</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-12">
          <h2 className="text-3xl font-bold mb-4">Start Making Homeschool Math Fun Today</h2>
          <p className="text-xl text-muted-foreground mb-6 max-w-2xl mx-auto">
            Join thousands of homeschool families using our free tools. No account needed - just click and start learning!
          </p>
          <Link to="/">
            <Button size="lg" className="text-lg px-8">
              Try Free Tools Now
            </Button>
          </Link>
        </section>
      </div>
    </Layout>
  );
};

export default EducationHomeschool;
